import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { DateTime } from 'luxon';
import { useFormContext, useWatch } from 'react-hook-form';
import type { EventGenerateFormValues } from '@/components/Event/EventGenerate.types';
import CountStepper from '@/components/FormFields/CountStepper';
import WelcomeStepFrame, {
  WelcomeStepScrollContent,
} from '@/components/Welcome/WelcomeStepFrame';
import {
  WelcomeActions,
  WelcomeCard,
  WelcomePrimaryAction,
} from '@/components/Welcome/WelcomeUI';
import generateGames from '@/utils/generateGames';

const MIN_SCHEDULE_OPTION = 1;
const MAX_SCHEDULE_OPTION = 3;

export default function EventGenerateOption() {
  const { eventId } = useLocalSearchParams<{ eventId: string }>();
  const { push } = useRouter();
  const { control, getValues, setValue } =
    useFormContext<EventGenerateFormValues>();
  const selectedTeams = useWatch({
    name: 'selectedTeams',
    control,
    defaultValue: [],
  });
  const initialGames = useWatch({
    name: 'initialGames',
    control,
    defaultValue: [],
  });
  const matchupCount = useWatch({
    name: 'matchupCount',
    control,
    defaultValue: 1,
  });
  const repeatEvery = useWatch({
    name: 'repeatEvery',
    control,
    defaultValue: { weeks: 1 },
  });
  const filledSlots = initialGames
    .filter((game) => game.datetime.trim().length > 0)
    .map((game) => ({
      datetime: game.datetime.trim(),
      location: game.location?.trim() || null,
    }));
  const hasInvalidSlot = initialGames.some((game) => {
    const datetime = game.datetime.trim();

    return datetime.length > 0 && !DateTime.fromISO(datetime).isValid;
  });
  const canContinue =
    matchupCount > 0 &&
    repeatEvery.weeks > 0 &&
    selectedTeams.length > 1 &&
    filledSlots.length > 0 &&
    !hasInvalidSlot;

  const setScheduleOption = (
    field: 'matchupCount' | 'repeatEvery.weeks',
    value: number,
  ) => {
    setValue(field, value, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('games', [], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };
  const decreaseMatchupCount = () => {
    setScheduleOption(
      'matchupCount',
      Math.max(matchupCount - 1, MIN_SCHEDULE_OPTION),
    );
  };
  const increaseMatchupCount = () => {
    setScheduleOption(
      'matchupCount',
      Math.min(matchupCount + 1, MAX_SCHEDULE_OPTION),
    );
  };
  const decreaseRepeatEveryWeeks = () => {
    setScheduleOption(
      'repeatEvery.weeks',
      Math.max(repeatEvery.weeks - 1, MIN_SCHEDULE_OPTION),
    );
  };
  const increaseRepeatEveryWeeks = () => {
    setScheduleOption(
      'repeatEvery.weeks',
      Math.min(repeatEvery.weeks + 1, MAX_SCHEDULE_OPTION),
    );
  };

  const onPressPreview = () => {
    if (!canContinue) {
      return;
    }

    const { games: generatedGames } = generateGames({
      parentId: eventId,
      teams: getValues('selectedTeams'),
      initialGames: filledSlots,
      numberOfMatchups: getValues('matchupCount'),
      repeatEvery: getValues('repeatEvery'),
      byeWeeks: true,
    });

    setValue('games', generatedGames, {
      shouldDirty: true,
      shouldValidate: true,
    });

    push({
      pathname: '/event/[eventId]/generate/preview',
      params: { eventId },
    });
  };

  return (
    <>
      <Stack.Screen options={{ title: 'Schedule Options' }} />
      <WelcomeStepFrame
        title="Choose schedule options"
        subtitle="These defaults create a simple round-robin schedule you can review before saving."
        footer={
          <WelcomeActions>
            <WelcomePrimaryAction
              label="Review games"
              disabled={!canContinue}
              onPress={onPressPreview}
            />
          </WelcomeActions>
        }
      >
        <WelcomeStepScrollContent>
          <WelcomeCard>
            <CountStepper
              decrementAccessibilityLabel="Decrease matchup count"
              incrementAccessibilityLabel="Increase matchup count"
              label="How many times should each team play each other?"
              max={MAX_SCHEDULE_OPTION}
              min={MIN_SCHEDULE_OPTION}
              onDecrement={decreaseMatchupCount}
              onIncrement={increaseMatchupCount}
              testID="event-generate-matchup-count"
              value={matchupCount}
            />

            <CountStepper
              decrementAccessibilityLabel="Decrease weeks between games"
              incrementAccessibilityLabel="Increase weeks between games"
              label="How many weeks between games?"
              max={MAX_SCHEDULE_OPTION}
              min={MIN_SCHEDULE_OPTION}
              onDecrement={decreaseRepeatEveryWeeks}
              onIncrement={increaseRepeatEveryWeeks}
              testID="event-generate-repeat-weeks"
              value={repeatEvery.weeks}
            />
          </WelcomeCard>
        </WelcomeStepScrollContent>
      </WelcomeStepFrame>
    </>
  );
}
